import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../order.schema';
import { ApiItems } from '../../types';

import { Card } from '../../style';
import { DeleteDiv, SubItemCard, DeleteButtonWrapper } from './style';

import ConfirmationModal from '../../../../../../components/ModalConfirmation';

import { FormMaterial } from './components/Forms/FormMaterial';
import { FormProduct } from './components/Forms/FormProduct';
import { FormResource } from './components/Forms/FormResource';
import { FormService } from './components/Forms/FormService';

interface SubItemProps {
  parentItemPath: `items.${number}`;
  subItemIndex: number;
  itemType: string;
  onDelete: () => void;
  itemOptionsFromApi: ApiItems[];
  formatOptionsList: string[];
  finishingOptionsList: string[];
}

const SubItem: React.FC<SubItemProps> = ({
  parentItemPath,
  subItemIndex,
  itemType,
  onDelete,
  itemOptionsFromApi,
  formatOptionsList,
  finishingOptionsList
}) => {
  const { formState: { errors }, watch, setValue, trigger } = useFormContext<OrderSchemaType>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const subItemPath: ItemPath = `${parentItemPath}.subItems.${subItemIndex}` as ItemPath;

  useEffect(() => {
    const type = watch(`${subItemPath}.itemType` as any) as string | undefined;
    const qtyPaths: Record<string, string> = {
      MATERIAL: `${subItemPath}.feedstock.quantidade` as any,
      PRODUCT: `${subItemPath}.finalProduct.quantidade` as any,
      RESOURCE: `${subItemPath}.resource.quantidade` as any,
      SERVICE: `${subItemPath}.stage.quantidade` as any,
    };
    const idSetters: Record<string, () => void> = {
      MATERIAL: () => {},
      PRODUCT: () => {},
      RESOURCE: () => {},
      SERVICE: () => {},
    };
    const qtyPath = type ? qtyPaths[type] : undefined;
    if (qtyPath) {
      const q = Number(watch(qtyPath as any));
      if (!isNaN(q)) setValue((`${subItemPath}.quantity`) as any, q, { shouldDirty: true, shouldValidate: false });
    }
  }, [watch(`${subItemPath}.feedstock.quantidade` as any), watch(`${subItemPath}.finalProduct.quantidade` as any), watch(`${subItemPath}.resource.quantidade` as any), watch(`${subItemPath}.stage.quantidade` as any)]);

  const handleConfirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  const renderItemContent = () => {
    switch (itemType) {
      case 'MATERIAL':
        return <FormMaterial
          itemPath={subItemPath}
          itemOptionsFromApi={itemOptionsFromApi}
          formatOptionsList={formatOptionsList}
          finishingOptionsList={finishingOptionsList}
        />;
      case 'PRODUCT':
        return <FormProduct itemPath={subItemPath} />;
      case 'RESOURCE':
        return <FormResource itemPath={subItemPath} />;
      case 'SERVICE':
        return <FormService itemPath={subItemPath} />;
      default:
        return null;
    }
  };

  const typeInPortuguese = {
    SERVICE: 'Serviço',
    PRODUCT: 'Produto Final',
    RESOURCE: 'Insumo',
    MATERIAL: 'Matéria Prima'
  }[itemType] || itemType;

  return (
    <>
      <SubItemCard as={Card} style={{ padding: '16px' }}>
        <DeleteButtonWrapper>
          <DeleteDiv onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={18} />
          </DeleteDiv>
        </DeleteButtonWrapper>
        {renderItemContent()}
      </SubItemCard>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={`Tem certeza que deseja excluir o ${typeInPortuguese}?`}
        message="Essa ação não poderá ser desfeita."
        actions={[
          { label: "Não, voltar", onClick: () => setShowDeleteConfirm(false), variant: 'outline' },
          { label: "Sim, excluir", onClick: handleConfirmDelete, variant: 'danger' }
        ]}
      />
    </>
  );
};

export default SubItem;